import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';

interface Option {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ options, value, onChange, placeholder = 'Select...', className }) => {
  value = Array.isArray(value) ? value : [];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue: string) => {
    onChange(value.filter(v => v !== optionValue));
  };

  const selectedOptions = options.filter(opt => value.includes(opt.value));

  return (
    <div className={`relative ${className || ''}`} ref={ref}>
      <button
        type="button"
        className="w-full flex items-center justify-between border border-sauna-orange/30 rounded-lg px-4 py-2 bg-white text-sauna-navy text-base focus:outline-none focus:ring-2 focus:ring-sauna-orange/30"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedOptions.length ? 'font-semibold' : 'text-gray-400'}>
          {selectedOptions.length ? `+ ${selectedOptions.length} selected` : placeholder}
        </span>
        <ChevronDown className="h-5 w-5 text-sauna-orange ml-2" />
      </button>
      {open && (
        <div className="absolute z-20 mt-2 w-full bg-white border border-sauna-orange/30 rounded-lg shadow-lg max-h-60 overflow-auto">
          <ul tabIndex={-1} role="listbox" className="py-2">
            {options.map(opt => (
              <li
                key={opt.value}
                role="option"
                aria-selected={value.includes(opt.value)}
                className={`flex items-center px-4 py-2 cursor-pointer hover:bg-sauna-beige ${value.includes(opt.value) ? 'bg-sauna-beige/60' : ''}`}
                onClick={() => handleToggle(opt.value)}
              >
                <span className={`mr-2 w-5 h-5 flex items-center justify-center border rounded ${value.includes(opt.value) ? 'bg-sauna-orange border-sauna-orange text-white' : 'border-gray-300 bg-white'}`}>
                  {value.includes(opt.value) && <Check className="h-4 w-4" />}
                </span>
                <span>{opt.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-2">
        {selectedOptions.map(opt => (
          <span key={opt.value} className="bg-sauna-orange/10 text-sauna-orange px-3 py-1 rounded-full text-sm flex items-center gap-1">
            {opt.label}
            <button type="button" onClick={() => handleRemove(opt.value)} className="ml-1 text-sauna-orange hover:text-orange-700 focus:outline-none">
              <X className="h-4 w-4" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export default MultiSelect; 